import { useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useNavigationHistory } from "./NavigationHistoryProvider";
import { ShallowEntity } from "../../FpPages/RMS/Dashboard/types";

/**
 * Hook that provides a safe delete function that:
 * 1. Removes the current URL from navigation history
 * 2. Navigates to the previous page or fallback
 * 3. Handles the deletion operation
 */
export const useDeleteWithHistory = () => {
  const location = useLocation();
  const { goBackWithoutPop, removeFromHistory, removeFromHistoryByPattern } =
    useNavigationHistory();

  const deleteAndNavigate = useCallback(
    async (
      deleteFunction: () => Promise<any>,
      options: {
        fallback?: string;
        onSuccess?: () => void | Promise<void>;
      } = {}
    ) => {
      const { fallback = "/rms", onSuccess } = options;

      try {
        // Execute the delete function
        await deleteFunction();

        // Execute any additional success callbacks (like cache invalidation)
        if (onSuccess) {
          await onSuccess();
        }

        // Get current URL to remove from history
        const currentUrl = location.pathname + location.search + location.hash;

        // Remove current URL from history stack
        removeFromHistory(currentUrl);

        // Navigate back to previous page or fallback (without popping again)
        goBackWithoutPop(fallback);
      } catch (error) {
        console.error("Delete operation failed:", error);
        throw error; // Re-throw so calling component can handle it
      }
    },
    [location, goBackWithoutPop, removeFromHistory]
  );

  const deleteEntityAndNavigate = useCallback(
    async (
      entity: ShallowEntity,
      deleteFunction: () => Promise<any>,
      options: {
        fallback?: string;
      } = {}
    ) => {
      const { fallback = "/rms" } = options;

      try {
        // Execute the delete function
        await deleteFunction();

        // Create pattern to match all URLs related to this entity
        // This includes the main entity page and all its tabs (facts, interactions, etc.)
        const entityUrlPattern = (url: string) => {
          // Match URLs like /rms/vehicle/company-name-abc123def456, /rms/vehicle/company-name-abc123def456/facts, etc.
          // The URL uses slugs, so we need to match the entity kind and check if the slug contains the entity ID
          const entityKindPath = `/rms/${entity.kind}/`;
          if (!url.startsWith(entityKindPath)) {
            return false;
          }

          // Extract the slug part after the entity kind
          const slugPart = url.substring(entityKindPath.length);
          // Remove any tab path (everything after the first slash)
          const slug = slugPart.split("/")[0];

          // Check if the slug ends with the entity ID (last 12 characters)
          const entityIdSuffix = entity.id.slice(-12);
          return slug.endsWith(entityIdSuffix);
        };

        // Remove all entity-related URLs from history stack
        removeFromHistoryByPattern(entityUrlPattern);

        // Navigate back to previous page or fallback (without popping again)
        goBackWithoutPop(fallback);
      } catch (error) {
        console.error("Delete entity operation failed:", error);
        throw error; // Re-throw so calling component can handle it
      }
    },
    [goBackWithoutPop, removeFromHistoryByPattern]
  );

  return { deleteAndNavigate, deleteEntityAndNavigate };
};
